// Platform adapter (architecture §3.1) — the web implementation. A `capacitor.ts` sibling
// implements the same interface when the store shells ship (architecture §11).
export interface Platform {
  name: 'web' | 'capacitor';
  share(data: { title?: string; text?: string; url: string }): Promise<'shared' | 'copied'>;
  registerPush(vapidPublicKey: string): Promise<PushSubscriptionJSON | null>;
  unregisterPush(): Promise<string | null>;
  /** Endpoint of this browser's push subscription, if it has one. */
  currentPushEndpoint(): Promise<string | null>;
  canInstall(): boolean;
  promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'>;
  isIosSafari(): boolean;
  isStandalone(): boolean;
}

let installPrompt:
  | (Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> })
  | null = null;

// Screens subscribe so an install button appears whenever the browser offers one,
// not only if the event happened to fire before they rendered.
const installListeners = new Set<() => void>();
const notifyInstall = () => {
  for (const listener of installListeners) listener();
};

export function onInstallAvailabilityChange(listener: () => void) {
  installListeners.add(listener);
  return () => {
    installListeners.delete(listener);
  };
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    installPrompt = event as never;
    notifyInstall();
  });
  window.addEventListener('appinstalled', () => {
    installPrompt = null;
    notifyInstall();
  });
}

/** The service worker registers on load in production only; never wait forever for it. */
async function serviceWorkerReady(timeoutMs = 4000): Promise<ServiceWorkerRegistration | null> {
  const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs));
  return Promise.race([navigator.serviceWorker.ready, timeout]);
}

function sameKey(current: ArrayBuffer | null, wanted: Uint8Array) {
  if (!current) return false;
  const a = new Uint8Array(current);
  return a.length === wanted.length && a.every((byte, i) => byte === wanted[i]);
}

function urlBase64ToUint8Array(base64: string) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export const platform: Platform = {
  name: 'web',

  /** App Flow §5 — system share sheet, copy as the fallback. */
  async share(data) {
    if (navigator.share) {
      try {
        await navigator.share(data);
        return 'shared';
      } catch {
        /* user dismissed — fall through to copy */
      }
    }
    await navigator.clipboard.writeText(data.url);
    return 'copied';
  },

  async registerPush(vapidPublicKey) {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;
    const registration = await serviceWorkerReady();
    if (!registration) return null;
    const key = urlBase64ToUint8Array(vapidPublicKey);
    let existing = await registration.pushManager.getSubscription();
    // A subscription made with another key can never receive our pushes: replace it.
    if (existing && !sameKey(existing.options.applicationServerKey, key)) {
      await existing.unsubscribe();
      existing = null;
    }
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: key as BufferSource,
      }));
    return subscription.toJSON();
  },

  async currentPushEndpoint() {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
    const registration = await serviceWorkerReady();
    const subscription = await registration?.pushManager.getSubscription();
    return subscription?.endpoint ?? null;
  },

  async unregisterPush() {
    if (!('serviceWorker' in navigator)) return null;
    const registration = await serviceWorkerReady();
    if (!registration) return null;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return null;
    const endpoint = subscription.endpoint;
    await subscription.unsubscribe();
    return endpoint;
  },

  canInstall() {
    return installPrompt !== null;
  },

  async promptInstall() {
    if (!installPrompt) return 'unavailable';
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    installPrompt = null;
    notifyInstall();
    return choice.outcome === 'accepted' ? 'accepted' : 'dismissed';
  },

  /** iOS Safari cannot push until the app is on the Home Screen (architecture §3.7). */
  isIosSafari() {
    const ua = navigator.userAgent;
    return /iPad|iPhone|iPod/.test(ua) && /Safari/.test(ua) && !/CriOS|FxiOS/.test(ua);
  },

  isStandalone() {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as { standalone?: boolean }).standalone === true
    );
  },
};
